/**
 * Public Twilio Voice SDK edge locations for @twilio/voice-sdk@2.18.3.
 *
 * The installed SDK defines an Edge enum (including Interconnect *-ix values) in
 * its regions module, but only exports Edge as a type from '@twilio/voice-sdk'.
 * This list mirrors the public (non-Interconnect) Edge values from that SDK
 * version and matches the accounts__voice_edges Postgres enum.
 */
export const TWILIO_PUBLIC_VOICE_EDGES = [
    'ashburn',
    'dublin',
    'frankfurt',
    'roaming',
    'sao-paulo',
    'singapore',
    'sydney',
    'tokyo',
    'umatilla',
] as const;

export type TwilioPublicVoiceEdge = (typeof TWILIO_PUBLIC_VOICE_EDGES)[number];

/** Twilio Device default when account.voice_edge is null/undefined. */
export const TWILIO_DEFAULT_VOICE_EDGE: TwilioPublicVoiceEdge = 'roaming';

/**
 * Labels include geographic locations from Twilio Edge Locations docs:
 * https://www.twilio.com/docs/global-infrastructure/edge-locations
 */
const TWILIO_PUBLIC_VOICE_EDGE_LABELS: Record<TwilioPublicVoiceEdge, string> = {
    ashburn: 'Ashburn — US East Coast (Virginia)',
    dublin: 'Dublin — Ireland',
    frankfurt: 'Frankfurt',
    roaming: 'Roaming — Global Low Latency',
    'sao-paulo': 'São Paulo — Brazil',
    singapore: 'Singapore',
    sydney: 'Sydney — Australia',
    tokyo: 'Tokyo — Japan',
    umatilla: 'Umatilla — US West Coast (Oregon)',
};

export type VoiceEdgeSelectOption = {
    key: string;
    value: string;
};

/**
 * Super Admin dropdown options (public Twilio edges, including roaming).
 * Roaming is listed first as the Global Low Latency choice.
 */
export function getTwilioVoiceEdgeSelectOptions(): VoiceEdgeSelectOption[] {
    const options: VoiceEdgeSelectOption[] = [
        {
            key: TWILIO_PUBLIC_VOICE_EDGE_LABELS.roaming,
            value: TWILIO_DEFAULT_VOICE_EDGE,
        },
    ];

    for (const edge of TWILIO_PUBLIC_VOICE_EDGES) {
        if (edge === TWILIO_DEFAULT_VOICE_EDGE) {
            continue;
        }
        options.push({
            key: TWILIO_PUBLIC_VOICE_EDGE_LABELS[edge],
            value: edge,
        });
    }

    return options;
}

export function isPublicTwilioVoiceEdge(edge: string): edge is TwilioPublicVoiceEdge {
    return (TWILIO_PUBLIC_VOICE_EDGES as readonly string[]).includes(edge);
}

/**
 * Resolve account.voice_edge to the effective Twilio Device edge.
 * NULL/undefined -> roaming. Invalid explicit values warn and fall back to roaming.
 */
export function getTwilioVoiceEdge(voiceEdge: string | null | undefined): string {
    if (voiceEdge == null || voiceEdge === '') {
        return TWILIO_DEFAULT_VOICE_EDGE;
    }

    if (!isPublicTwilioVoiceEdge(voiceEdge)) {
        console.warn('Invalid account voice_edge; falling back to roaming', {
            configuredVoiceEdge: voiceEdge,
            provider: 'twilio',
            effectiveVoiceEdge: TWILIO_DEFAULT_VOICE_EDGE,
        });
        return TWILIO_DEFAULT_VOICE_EDGE;
    }

    return voiceEdge;
}
